import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import { getCollection } from 'api/market-view-api';
import type { CollectionResponse } from 'api/market-view-api';
import { createReport, modifyReport, deleteReport } from 'api/reports-api';
import type { ReportDto, ReportRequest } from 'api/reports-api';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';

export type { ReportDto } from 'api/reports-api';

export const adminReportsKeys = {
  all: ['admin-reports'] as const,
  lists: () => [...adminReportsKeys.all, 'list'] as const,
  list: (params?: AdminReportsListParams) =>
    [...adminReportsKeys.lists(), params] as const,
};

export interface AdminReportsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

interface UseAdminReportsListArgs extends AdminReportsListParams {
  enabled?: boolean;
}

export const useAdminReportsList = ({
  pageSize = 10,
  pageIndex = 0,
  sortBy = [],
  filters,
  enabled = true,
}: UseAdminReportsListArgs): UseQueryResult<
  CollectionResponse<ReportDto[]>,
  Error
> =>
  useQuery({
    queryKey: adminReportsKeys.list({ pageSize, pageIndex, sortBy, filters }),
    queryFn: async () => {
      const response = await getCollection('adminReports', {
        pageSize,
        pageIndex,
        sortBy,
        filters,
      });

      return response as CollectionResponse<ReportDto[]>;
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

interface CreateReportVariables {
  userId: string;
  reportData: ReportRequest;
}

export const useCreateReport = (): UseMutationResult<
  { data: ReportDto },
  Error,
  CreateReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportData }: CreateReportVariables) =>
      createReport({ userId, reportData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};

interface ModifyReportVariables {
  userId: string;
  reportId: string | number;
  reportData: ReportRequest;
}

export const useModifyReport = (): UseMutationResult<
  { data: ReportDto },
  Error,
  ModifyReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportId, reportData }: ModifyReportVariables) =>
      modifyReport({ userId, reportId, reportData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};

interface DeleteReportVariables {
  userId: string;
  reportId: string | number;
}

export const useDeleteReport = (): UseMutationResult<
  unknown,
  Error,
  DeleteReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportId }: DeleteReportVariables) =>
      deleteReport({ userId, reportId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};
